// =============================================================================
// io/sync/propUniverse.ts — a five-column slice of the WHOLE book, for Class L.
// -----------------------------------------------------------------------------
// Balance, peak, drawdown floor and buffer, profit-target progress, consistency,
// min-trading-days and position-size `everOver`. Every one is a function of the
// ENTIRE trade history, and under a bound every one is wrong in the OPTIMISTIC
// direction and self-consistent while wrong (D47):
//
//   • the drawdown buffer reads TOO LARGE — more room than actually exists
//   • consistency reports a FALSE PASS on a payout gate
//   • `everOver` flips true → false: an account already over its contract cap
//     reads clean
//
// A prop trader acts on all three. That is why the Prop Report was blocked rather
// than shipped with a caveat.
//
// ── WHY A DEDICATED READ, AND NOT A RECEIPT ON `loadTradesInRange` ──
//
// The obvious move is to make the range read return a `ReadReceipt` so a surface
// can mint a `WholeBook` when the user happens to have picked "All time". Three
// things are wrong with it:
//
//   1. The Prop Report must not depend on the date chip AT ALL. The old client is
//      explicit — its year-to-date anchor exists "without depending on the
//      trade-period chip (which is trade-centric, not account-centric)". A report
//      that answers "am I safe?" differently because a filter above it says
//      "This week" is not a compliance view.
//   2. `RangeBounds` can express `{ startDate: null, endDate: '2026-06-01' }` —
//      the "Until …" custom range — which reaches back forever and stops short at
//      the top. `ReadReceipt` models a `since` and has no `until`, so that read
//      cannot be described honestly by the existing type. Encoding it as
//      `since: ''` would make `coversWindow` answer true for every window.
//   3. `loadTradesInRange` pulls 24 columns. Across the whole book that is the
//      mobile-transfer cost D47 measured — 2.00 MB, ~1.6 s on 4G — to compute
//      five numbers per account.
//
// So this follows `coaching.ts`: no bounds parameter, pages to exhaustion, throws
// on the first bad page. There is no way to call it and receive less than the
// whole book.
//
// ── ⚠ AND YET IT RETURNS A RECEIPT, WHERE `coaching.ts` ARGUES AGAINST ONE ──
//
// That file says a receipt which can only ever report `coversWholeBook: true`
// proves nothing, and it is right — as EVIDENCE. This one is not evidence. It is
// the token `asWholeBook` demands before it will mint the type that Class-L
// selectors accept, so without it the caller's only route to a `WholeBook` is the
// `as` cast that the runtime half exists to survive.
//
// The distinction is worth keeping straight: `coaching.ts` returns rows nobody
// scopes, this one returns rows every consumer must scope, and the receipt is the
// key rather than the proof.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { TRADE_COLUMNS, type Trade, type TradeColumnKey } from '../../domain/types';
import { PAGE_ROWS } from './load';
import { PROJECTION_TAINT } from './hydration';
import { wholeBookReceipt, type ReadReceipt } from './receipt';

/**
 * `AccountTrade` — `DatedTrade` plus the three fields the account maths reads —
 * and the three the tilt banner reads. Eight columns.
 *
 * ⚠ `sessionDate` is NOT optional even though most callers pass `mode` as
 * calendar. `effectiveTradeDate` reads it under session mode, and an account
 * whose attempt started on a Monday would otherwise include Sunday-evening
 * trades that belong to Monday's session — moving the balance the drawdown floor
 * is measured from.
 *
 * ⚠ `qty` is here for `computePositionSizeUsage`'s `everOver`, which is a breach
 * flag rather than a statistic: it answers "has this account EVER exceeded its
 * contract cap", and it is the one number where a missing row flips a true to a
 * false.
 *
 * ── ⚠ THE LAST THREE ARE THE TILT BANNER'S, AND THEY ARE WHY THIS IS EIGHT ──
 *
 * `timeIn` orders the book and defines the 24h / 4h windows; `grade` and
 * `booleans` carry the two signals the trader writes themselves. All three are
 * Class L for the same reason as the rest: every tilt signal compares a recent
 * window against an ALL-TIME baseline, so a bounded read moves the baseline
 * toward the window and the warning silently stops firing. See `domain/propTilt`.
 *
 * ⚠ `booleans` is a JSONB blob rather than a scalar, so it is the one column here
 * with no fixed width. Two flags are read out of it (`fomoTrade`, `revengeTrade`)
 * and PostgREST has no way to project a single key, so the whole object crosses
 * the wire. On Josh's 820 rows that is immaterial; on a 50,000-trade book it is
 * the first thing to measure if this read gets slow.
 */
export const PROP_KEYS = [
  'accountId', 'date', 'sessionDate', 'netPnl', 'qty',
  'timeIn', 'grade', 'booleans',
] as const satisfies readonly TradeColumnKey[];

export type PropRow = Pick<Trade, (typeof PROP_KEYS)[number]>;

const PROP_SELECT = PROP_KEYS.map((k) => TRADE_COLUMNS[k]).join(',');

export function propRowFromDb(r: Record<string, unknown>): PropRow {
  const out: Record<string, unknown> = {
    accountId: r['account_id'] ?? null,
    date: r['date'] ?? null,
    sessionDate: r['session_date'] ?? null,
    // ⚠ `?? null`, not `|| null` — a 0 is a real scratch, and the balance
    // timeline counts it as a trading day for min-trading-days.
    netPnl: r['net_pnl'] ?? null,
    qty: r['qty'] ?? null,
    timeIn: r['time_in'] ?? null,
    // ⚠ `?? ''` matches `Trade.grade`, which is a non-null string. An ungraded
    // trade is `''`, and `GRADE_POINTS['']` is undefined, so it drops out of the
    // grade-decay average rather than scoring as an F.
    grade: r['grade'] ?? '',
    // ⚠ `?? {}` for the same reason `booleans` is non-null on `Trade`: every
    // reader does `booleans?.fomoTrade === true`, and a null here would work
    // today and break the first time someone writes `booleans.fomoTrade`.
    booleans: r['booleans'] ?? {},
  };

  // ⚠ TAINTED like every other projection: 8 of 48 columns. Nothing here is
  // editable today, which is exactly when the marker is free to add and exactly
  // when it gets forgotten.
  out[PROJECTION_TAINT] = 'prop';

  return out as unknown as PropRow;
}

export interface PropUniverseResult {
  rows: PropRow[];
  /**
   * ⚠ Always whole-book, by construction. It exists so `asWholeBook` has
   * something to accept — see the header.
   */
  receipt: ReadReceipt;
}

/**
 * Every trade the user has, in five columns.
 *
 * ⚠ Throws on the first bad page rather than returning what arrived. A short
 * result here does not look short — it produces a drawdown buffer with MORE room
 * than the account has, on the screen a trader checks before sizing up.
 */
export async function loadPropUniverse(sb: SupabaseClient): Promise<PropUniverseResult> {
  const rows: PropRow[] = [];
  let pages = 0;

  for (let from = 0; ; from += PAGE_ROWS) {
    const { data, error } = await sb
      .from('trades')
      .select(PROP_SELECT)
      // ⚠ ORDERED — what makes `range()` paging correct rather than decorative.
      // Over an unordered query a concurrent insert shifts rows across a page
      // boundary, dropping one and duplicating another with no error. A dropped
      // LOSS is the optimistic-direction failure this whole module is about.
      .order('id', { ascending: true })
      .range(from, from + PAGE_ROWS - 1);
    pages++;

    if (error) {
      throw new Error(
        `loadPropUniverse: read failed on page ${pages}: ${error.message}. Refusing to ` +
          `return a partial book — every number the Prop Report derives from it (balance, ` +
          `peak, drawdown floor and buffer, consistency, position-size breaches) would be ` +
          `wrong in the OPTIMISTIC direction and agree with itself while wrong.`,
      );
    }
    if (!data || !data.length) break;

    for (const r of data as unknown as Record<string, unknown>[]) rows.push(propRowFromDb(r));
    if (data.length < PAGE_ROWS) break; // short page = last page
  }

  return { rows, receipt: wholeBookReceipt(pages) };
}
